import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCcw } from "lucide-react";

import axiosInstance from "src/common/axiosInstance";

import WorkflowActions from "./LeadStagePanel/WorkflowActions/WorkflowActions";
import WorkflowTimeline from "./LeadStagePanel/WorkflowTimeline";

function JourneySkeleton({ error, onRetry, starting }) {
    return (
        <section aria-busy={!error} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 px-5 py-5 sm:px-6">
                <div className="animate-pulse">
                    <div className="h-3 w-28 rounded-full bg-white/20" />
                    <div className="mt-4 h-3 w-20 rounded-full bg-white/10" />
                    <div className="mt-2 h-6 w-52 rounded-lg bg-white/20" />
                </div>
            </div>

            <div className="space-y-3 p-4 sm:p-5">
                {error ? (
                    <div className="flex items-center justify-between gap-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                        <span className="text-sm">{error}</span>
                        <button type="button" onClick={onRetry} className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold hover:bg-rose-100">
                            <RefreshCcw className="h-3.5 w-3.5" /> Retry
                        </button>
                    </div>
                ) : (
                    <div className="animate-pulse space-y-3">
                        {[0, 1].map((item) => (
                            <div key={item} className="flex items-center gap-3 rounded-xl border border-slate-100 p-3">
                                <div className="h-9 w-9 rounded-xl bg-slate-100" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-2.5 w-24 rounded-full bg-slate-100" />
                                    <div className="h-4 w-2/5 rounded-full bg-slate-200" />
                                </div>
                                <div className="h-5 w-5 rounded bg-slate-100" />
                            </div>
                        ))}
                        <p className="sr-only">{starting ? "Preparing lead journey" : "Loading lead journey"}</p>
                    </div>
                )}
            </div>
        </section>
    );
}

export default function LeadStagePanel({ leadId, onWorkflowChanged }) {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [startingWorkflow, setStartingWorkflow] = useState(false);
    const [workflow, setWorkflow] = useState(null);
    const [history, setHistory] = useState([]);
    const [error, setError] = useState("");
    const hasAttemptedStart = useRef(false);
    const requestController = useRef(null);

    const fetchJourney = useCallback(async (signal) => {
        try {
            const response = await axiosInstance.get(
                `/lead-workflow/${leadId}/available-transitions`,
                { signal },
            );
            const journey = response.data.data;

            setWorkflow(journey);
            setHistory(journey?.history ?? []);

            return journey;
        } catch (requestError) {
            if (requestError?.response?.status === 404) {
                setWorkflow(null);
                setHistory([]);

                return null;
            }

            throw requestError;
        }
    }, [leadId]);

    const loadWorkflow = useCallback(async (silent = false) => {
        requestController.current?.abort();
        const controller = new AbortController();
        requestController.current = controller;

        if (silent) {
            setRefreshing(true);
        } else {
            setRefreshing(false);
            setLoading(true);
        }
        setError("");

        try {
            await fetchJourney(controller.signal);
        } catch (requestError) {
            if (requestError?.code === "ERR_CANCELED") return;

            console.error(requestError);
            setError(requestError?.response?.data?.message || "Unable to load workflow.");
        } finally {
            if (!silent && requestController.current === controller) setLoading(false);
            if (silent && requestController.current === controller) setRefreshing(false);
            if (requestController.current === controller) requestController.current = null;
        }
    }, [fetchJourney]);

    useEffect(() => {
        loadWorkflow();

        return () => requestController.current?.abort();
    }, [loadWorkflow]);

    const startWorkflow = useCallback(async () => {
        try {
            setStartingWorkflow(true);
            await axiosInstance.post("/lead-workflow/start", { lead_id: leadId });
            await loadWorkflow();
            await onWorkflowChanged?.();
        } catch (requestError) {
            console.error("Start Workflow", requestError.response);
            setError(requestError.response?.data?.message || "Unable to start workflow.");
        } finally {
            setStartingWorkflow(false);
        }
    }, [leadId, loadWorkflow, onWorkflowChanged]);

    useEffect(() => {
        if (!loading && !workflow && !startingWorkflow && !hasAttemptedStart.current) {
            hasAttemptedStart.current = true;
            startWorkflow();
        }
    }, [loading, workflow, startingWorkflow, startWorkflow]);

    if (loading || !workflow) {
        return <JourneySkeleton error={error} starting={startingWorkflow} onRetry={() => loadWorkflow()} />;
    }

    return (
        <div className="space-y-6">
            <WorkflowActions
                leadId={leadId}
                workflow={workflow}
                refreshing={refreshing}
                onRefresh={() => loadWorkflow(true)}
                onWorkflowChanged={async () => {
                    await loadWorkflow(true);
                    await onWorkflowChanged?.();
                }}
            />

            <div id="workflow-timeline">
                <WorkflowTimeline history={history} />
            </div>

            {error && (
                <div className="flex items-center justify-between gap-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                    <span className="text-sm">{error}</span>
                    <button type="button" onClick={() => loadWorkflow(true)} className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 text-xs font-semibold hover:bg-rose-100">
                        <RefreshCcw className="h-3.5 w-3.5" /> Retry
                    </button>
                </div>
            )}
        </div>
    );
}
